/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { useEffect, useState } from "react";
import { changeBottomNavigationElements, changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import updateElement from "../functions/updateElement";
import Spinner from "../components/Spinner";
import SimpleResourceFieldViewer from "./SimpleResourceFieldViewer";
import IdentifierEntry, { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import getIdFromReference from "../functions/getIdFromReference";
import getFhirJson from "../functions/getFhirJsonAPI";
import { selectCurrentUserApiToken } from "../slices/currentUser/currentUserSlice";

const addResourcesToBundle = async (bundleReferencesState, setBundleResourceState, apiToken) => {
    if (bundleReferencesState?.references?.length > 0) {
        let bundleEntries = [];
        for (const reference of bundleReferencesState.references) {
            let foi = getIdFromReference(reference);
            if (foi && !isNaN(foi)) {
                let resource = await getFhirJson(foi, null, apiToken, false);
                if (resource?.id === foi) {
                    let entry = {};
                    if (resource.url) {
                        entry.fullUrl = resource.url;
                    } else if (resource.extension) {
                        for (let extension of resource.extension) {
                            if (extension.url === "http://hl7.org/fhir/StructureDefinition/artifact-url") {
                                entry.fullUrl = extension.valueUri;
                                break;
                            }
                        }
                    }
                    entry.resource = resource;
                    bundleEntries.push(entry);
                }
            }
        };
        setBundleResourceState(prevState => { return { ...prevState, entry: bundleEntries } });
    }
}

const BundleDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const apiToken = useSelector(selectCurrentUserApiToken);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const [bundleResourceState, setBundleResourceState] = useState({});
    const [bundleReferencesState, setBundleReferencesState] = useState({ "references": [], "loaded": false });

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Bundle Metadata", "Bundle Entries"]));
        dispatch(changeBottomNavigationElements(["JSON Outline"]));
        dispatch(changeBrowserTabTitle("FEvIR Bundle - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setBundleResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setBundleResourceState(resourceJson);
            if (!bundleReferencesState?.loaded) {
                let bundleReferences = [];
                if (resourceJson?.entry) {
                    bundleReferences = resourceJson.entry.map(entry => {
                        let resource = entry.resource || {};
                        let type = resource.resourceType || "MISSINGTYPE";
                        let display = resource.title || "";
                        if (!display && resource.name) {
                            if (typeof resource.name === "string") {
                                display = resource.name;
                            } else if (resource.name.text) {
                                display = resource.name.text;
                            } else {
                                display = resource.name.toString();
                            }
                        }
                        if (!display) {
                            display = "[Unnamed " + type + "]";
                        }
                        let id = resource.id || "MISSINGID";
                        return { "reference": type + "/" + id, "type": type, "display": display };
                    });
                }
                setBundleReferencesState({
                    "references": bundleReferences, "loaded": true, "firstLoad": false
                });
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Bundle " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(bundleResourceState, "type", "code", parentResourceDetail,
                dispatch);
        }
    }, [bundleResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(bundleResourceState, "identifier", "Identifier", parentResourceDetail,
                dispatch);
        }
    }, [bundleResourceState.identifier]);

    useEffect(() => {
        if (bundleReferencesState?.loaded === true) {
            if (bundleReferencesState.firstLoad === false) {
                setBundleReferencesState(prevState => { return { ...prevState, "firstLoad": true }; });
            } else {
                addResourcesToBundle(bundleReferencesState, setBundleResourceState, apiToken);
            }
        }
    }, [bundleReferencesState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(bundleResourceState, "entry", "ArrayOfBundleEntry", parentResourceDetail,
                dispatch);
        }
    }, [bundleResourceState.entry]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {bundleResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="bundle-metadata">Bundle Metadata</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <CodeEntry elementName='type' fieldLabel='Type'
                                allowedValues={['document', 'message', 'transaction', 'transaction-response',
                                    'batch', 'batch-response',
                                    'history', 'searchset', 'collection', 'subscription-notification']}
                                startingValue={bundleResourceState.type}
                                setResourceState={setBundleResourceState} />
                        </>
                        :
                        <>
                            {bundleResourceState.type && <p><b>Type: </b>{bundleResourceState.type}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <IdentifierEntry elementName='identifier' fieldLabel='Identifier'
                                startingValue={bundleResourceState.identifier || null} startCollapsed
                                setResourceState={setBundleResourceState} />
                        </>
                        :
                        <>
                            {bundleResourceState.identifier && <>
                                {Array.isArray(bundleResourceState.identifier) ?
                                    <>{bundleResourceState.identifier?.map((identifier, identifierIndex) => {
                                        return <div key={identifierIndex}>
                                            <div><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</div>
                                        </div>
                                    })}</>
                                    :
                                    <p><b>Identifier: </b>{displayStringFromFHIRIdentifier(bundleResourceState.identifier)}</p>}
                            </>}
                        </>
                    }
                    {bundleResourceState.timestamp && <p><b>Timestamp: </b>{bundleResourceState.timestamp}</p>}
                    {!isNaN(bundleResourceState.total) && <p><b>Number of entries: </b>{bundleResourceState.total}</p>}
                    {bundleResourceState.link?.map((link, linkIndex) => {
                        return <div key={linkIndex}>
                            <div><b>{link.relation} Link: </b><DisplayFromFHIRUri uri={link.url} /></div>
                        </div>
                    })}
                </div>
                <h3 id="bundle-entries">Bundle Entries</h3>
                <div style={{ marginLeft: "24px" }}>
                    {(editMode && bundleReferencesState?.loaded) && <>
                        <p>To manage the Bundle entries as a simple set of Resource references:</p>
                        <div style={{ marginLeft: "24px" }}>
                            <ArrayEntry datatype='Reference' elementName='references'
                                fieldLabel={'The Resources'}
                                startingValue={bundleReferencesState.references || []}
                                startCollapsed startEmptyArrayClosed enableCreation={true}
                                setResourceState={setBundleReferencesState} />
                        </div>
                    </>}
                    {Array.isArray(bundleResourceState.entry) &&
                        bundleResourceState.entry.map((entry, entryIndex) => {
                            return <div key={entryIndex}>
                                <p><b>Entry {entryIndex + 1}: </b></p>
                                <div style={{ marginLeft: "24px" }}>
                                    {entry.fullUrl && <div><b>URL: </b><DisplayFromFHIRUri uri={entry.fullUrl} />
                                        <br /><br /></div>}
                                    {entry.resource && <div>
                                        {entry.resource.resourceType && <p><b>Resource Type: </b>{entry.resource.resourceType}</p>}
                                        {entry.resource.title && <p><b>Resource Title: </b>{entry.resource.title}</p>}
                                        {(entry.resource.name && typeof entry.resource.name === "string") &&
                                            <p><b>Resource Name: </b>{entry.resource.name}</p>}
                                        {entry.resource.id && <p><b>Resource ID: </b>{entry.resource.id}</p>}
                                        {Array.isArray(entry.resource.identifier) &&
                                            entry.resource.identifier.map((identifier, identifierIndex) => {
                                                return <div key={identifierIndex}>
                                                    <div><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</div>
                                                </div>
                                            })}
                                    </div>}
                                </div>
                            </div>
                        })}
                    {(!bundleResourceState.entry || bundleResourceState.entry.length === 0) &&
                        <p>No entries.</p>}
                </div>
                <h3 id="json-outline">JSON Outline</h3>
                <SimpleResourceFieldViewer resource={bundleResourceState} parentElement={""} />
                <br /><br />
            </div>
        </>}
    </div>
}

export default BundleDisplay;